import { join } from 'path-browserify';
import matter from 'gray-matter';
import type { JSRuntimeFS } from './JSRuntime';

export interface Skill {
  /** Skill name (alphanumeric lowercase only) */
  name: string;
  /** Human-readable description of what the skill does */
  description: string;
  /** Full path to the skill directory */
  path: string;
  /** Plugin name that contains this skill */
  plugin: string;
}

export interface SkillFrontmatter {
  name: string;
  description: string;
  [key: string]: unknown;
}

/**
 * Validates a skill name (alphanumeric lowercase with hyphens)
 */
export function isValidSkillName(name: string): boolean {
  return /^[a-z0-9-]+$/.test(name);
}

/**
 * Get skills from a directory with a given plugin/source name
 */
async function getSkillsFromDirectory(
  fs: JSRuntimeFS,
  skillsDir: string,
  sourceName: string
): Promise<Skill[]> {
  const skills: Skill[] = [];

  try {
    const skillDirs = await fs.readdir(skillsDir);

    for (const skillDir of skillDirs) {
      const skillPath = join(skillsDir, skillDir);
      const stat = await fs.stat(skillPath);

      if (!stat.isDirectory()) continue;

      const skillFilePath = join(skillPath, 'SKILL.md');

      try {
        const skillContent = await fs.readFile(skillFilePath, 'utf8');
        const { data } = matter(skillContent);

        const frontmatter = data as SkillFrontmatter;

        // Validate required fields
        if (!frontmatter.name || !frontmatter.description) {
          console.warn(`Skill at ${skillPath} missing required frontmatter fields (name, description)`);
          continue;
        }

        // Validate skill name format
        if (!isValidSkillName(frontmatter.name)) {
          console.warn(`Skill at ${skillPath} has invalid name "${frontmatter.name}" (must be lowercase alphanumeric with hyphens)`);
          continue;
        }

        skills.push({
          name: frontmatter.name,
          description: frontmatter.description,
          path: skillPath,
          plugin: sourceName,
        });
      } catch {
        // SKILL.md not found or invalid, skip this directory
        continue;
      }
    }
  } catch {
    // Skills directory doesn't exist
    return [];
  }

  return skills;
}

/**
 * Get all skills from a single plugin
 */
export function getSkillsFromPlugin(
  fs: JSRuntimeFS,
  pluginsPath: string,
  pluginName: string
): Promise<Skill[]> {
  return getSkillsFromDirectory(fs, join(pluginsPath, pluginName, 'skills'), pluginName);
}

/**
 * Get all skills from a project's skills directory
 * Checks for skills in order: .agents/skills, .opencode/skills, .claude/skills, skills
 */
export async function getProjectSkills(
  fs: JSRuntimeFS,
  projectPath: string
): Promise<Skill[]> {
  const skillsPaths = [
    join(projectPath, '.agents', 'skills'),
    join(projectPath, '.opencode', 'skills'),
    join(projectPath, '.claude', 'skills'),
    join(projectPath, 'skills'),
  ];

  // Try each path in order and return skills from the first one that exists
  for (const skillsPath of skillsPaths) {
    try {
      const stat = await fs.stat(skillsPath);
      if (stat.isDirectory()) {
        return getSkillsFromDirectory(fs, skillsPath, 'project');
      }
    } catch {
      // Directory doesn't exist, try next one
      continue;
    }
  }

  // No skills directory found
  return [];
}

/**
 * Get all skills from all plugins (without project skills)
 */
export async function getPluginSkills(fs: JSRuntimeFS, pluginsPath: string): Promise<Skill[]> {
  const { getPlugins } = await import('./plugins');
  const plugins = await getPlugins(fs, pluginsPath);
  const allSkills: Skill[] = [];

  for (const plugin of plugins) {
    const skills = await getSkillsFromPlugin(fs, pluginsPath, plugin);
    allSkills.push(...skills);
  }

  return allSkills;
}

/**
 * Get all skills from both plugins and project, with project skills taking precedence
 */
export async function getAllSkills(
  fs: JSRuntimeFS,
  pluginsPath: string,
  projectPath: string
): Promise<Skill[]> {
  // Get skills from both sources
  const pluginSkills = await getPluginSkills(fs, pluginsPath);
  const projectSkills = await getProjectSkills(fs, projectPath);

  // Create a map to handle name collisions, with project skills taking precedence
  const skillsMap = new Map<string, Skill>();

  // Add plugin skills first
  for (const skill of pluginSkills) {
    skillsMap.set(skill.name, skill);
  }

  // Add project skills, overwriting any plugin skills with the same name
  for (const skill of projectSkills) {
    skillsMap.set(skill.name, skill);
  }

  // Return all skills as an array
  return Array.from(skillsMap.values());
}

/**
 * Find a skill by name across both plugins and project, with project skills taking precedence
 */
export async function findSkill(
  fs: JSRuntimeFS,
  pluginsPath: string,
  projectPath: string,
  skillName: string
): Promise<Skill | null> {
  const skills = await getAllSkills(fs, pluginsPath, projectPath);
  return skills.find(skill => skill.name === skillName) || null;
}

/**
 * Read the SKILL.md content for a skill
 */
export async function readSkillContent(fs: JSRuntimeFS, skill: Skill): Promise<string> {
  const skillFilePath = join(skill.path, 'SKILL.md');
  return await fs.readFile(skillFilePath, 'utf8');
}
